/* ---------------------------------------------------------
 * src/API/token-util.js
 * ---------------------------------------------------------
 *  Unified *user*-role auth storage  (100-year persistence)
 *
 *    • Browser / PWA   → js-cookie
 *    • Capacitor       → Preferences / Keychain
 *
 *  Call `initAuthStorage()` once at app start — before Axios mounts.
 * ------------------------------------------------------- */

import { Capacitor }                 from '@capacitor/core';
import { Preferences }               from '@capacitor/preferences';
import { App }                       from '@capacitor/app';
import Cookies                       from 'js-cookie';
import axios                         from 'axios';
import { ConfigValue }               from '../Config';
import { clearMemory as clearCreatorMem } from './creatorTokenUtil';   // ← wipe stale creator token

/* ─────────────────────────── Keys ─────────────────────────── */

const KEY               = ConfigValue.AUTH_TOKEN_KEY;            // "blitz-auth-token"
const USER_INFO_KEY     = ConfigValue.AUTH_USER_INFO;
const REFRESH_KEY       = `${KEY}-refresh`;

const CREATOR_TOKEN_KEY = ConfigValue.AUTH_CREATOR_TOKEN_KEY;
const CREATOR_INFO_KEY  = ConfigValue.AUTH_CREATOR_USER_INFO;

const BASE_API = process.env.REACT_APP_BASE_URL || 'https://blitz-app-u3rzf.ondigitalocean.app/api';
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const MIN_REFRESH_DELAY_MS = 15 * 1000;
const DEFAULT_TOKEN_LIFETIME_MS = 60 * 60 * 1000;

// Keep refresh tokens out of browser-accessible storage.
// Web refresh should rely on HttpOnly cookie sessions (`withCredentials`).
const canPersistRefreshToken = () => Capacitor.isNativePlatform();

/* 100-year cookie  ≈ 36 500 days */
const PERSIST_DAYS = 365 * 100;

/* ─────────────────── In-memory caches ────────────────────── */

let tokenCache    = null;   // string JWT
let userInfoCache = null;   // arbitrary user blob
let refreshTokenCache = null;
let refreshTimer = null;
let refreshInFlight = null;

function parseJwtExpMs(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;

    const payloadBase64 = parts[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const padded = payloadBase64 + '='.repeat((4 - (payloadBase64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    if (typeof payload.exp !== 'number') return null;

    return payload.exp * 1000;
  } catch {
    return null;
  }
}

function clearRefreshTimer() {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleProactiveRefresh(expiresInSec = null) {
  clearRefreshTimer();
  if (!tokenCache) return;

  let expMs = null;
  if (typeof expiresInSec === 'number' && expiresInSec > 0) {
    expMs = Date.now() + expiresInSec * 1000;
  } else {
    expMs = parseJwtExpMs(tokenCache);
  }

  if (!expMs) {
    expMs = Date.now() + DEFAULT_TOKEN_LIFETIME_MS;
  }

  const delay = Math.max(MIN_REFRESH_DELAY_MS, expMs - Date.now() - REFRESH_BUFFER_MS);
  refreshTimer = setTimeout(() => {
    void refreshAccessTokenNow();
  }, delay);
}

async function persistState({ token, info, refreshToken, clearCreator = false }) {
  tokenCache = token;
  userInfoCache = info;
  refreshTokenCache = canPersistRefreshToken() ? (refreshToken || null) : null;

  if (Capacitor.isNativePlatform()) {
    await Promise.all([
      token
        ? Preferences.set({ key: KEY, value: token })
        : Preferences.remove({ key: KEY }),
      info
        ? Preferences.set({ key: USER_INFO_KEY, value: JSON.stringify(info) })
        : Preferences.remove({ key: USER_INFO_KEY }),
      refreshTokenCache
        ? Preferences.set({ key: REFRESH_KEY, value: refreshTokenCache })
        : Preferences.remove({ key: REFRESH_KEY }),
      clearCreator ? Preferences.remove({ key: CREATOR_TOKEN_KEY }) : Promise.resolve(),
      clearCreator ? Preferences.remove({ key: CREATOR_INFO_KEY }) : Promise.resolve(),
    ]);
  } else {
    if (token) {
      Cookies.set(KEY, token,
        { expires: PERSIST_DAYS, secure: true, sameSite: 'Lax', path: '/' });
    } else {
      Cookies.remove(KEY);
    }

    if (info) {
      Cookies.set(USER_INFO_KEY, JSON.stringify(info),
        { expires: PERSIST_DAYS, secure: true, sameSite: 'Lax', path: '/' });
    } else {
      Cookies.remove(USER_INFO_KEY);
    }

    // Always clear legacy browser refresh-token cookie if present.
    Cookies.remove(REFRESH_KEY);

    if (clearCreator) {
      Cookies.remove(CREATOR_TOKEN_KEY);
      Cookies.remove(CREATOR_INFO_KEY);
    }
  }

  if (clearCreator) {
    clearCreatorMem();
  }
}

/* ─────────────────────── Boot / Hydrate ───────────────────── */

export async function initAuthStorage() {
  if (Capacitor.isNativePlatform()) {
    const [{ value: tok }, { value: info }, { value: refresh }] = await Promise.all([
      Preferences.get({ key: KEY }),
      Preferences.get({ key: USER_INFO_KEY }),
      Preferences.get({ key: REFRESH_KEY }),
    ]);
    tokenCache    = tok  || null;
    userInfoCache = info ? JSON.parse(info) : null;
    refreshTokenCache = refresh || null;
  } else {
    tokenCache    = Cookies.get(KEY) || null;
    const rawInfo = Cookies.get(USER_INFO_KEY);
    userInfoCache = rawInfo ? JSON.parse(rawInfo) : null;
    refreshTokenCache = null;
    Cookies.remove(REFRESH_KEY);
  }

  if (!tokenCache) {
    clearRefreshTimer();
    return;
  }

  const expMs = parseJwtExpMs(tokenCache);
  if (expMs && expMs <= Date.now()) {
    await removeAuthToken();
    return;
  }

  scheduleProactiveRefresh();

  // On iOS, background JS timers are suspended, so the proactive refresh setTimeout
  // may never fire if the app is backgrounded for > ~55 min. Re-check on every foreground.
  if (Capacitor.isNativePlatform()) {
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) return;
      const resumeExpMs = parseJwtExpMs(tokenCache);
      if (!resumeExpMs) return;
      if (resumeExpMs <= Date.now()) {
        void removeAuthToken();
      } else if (resumeExpMs - Date.now() < REFRESH_BUFFER_MS * 2) {
        void refreshAccessTokenNow();
      } else {
        scheduleProactiveRefresh(); // reschedule in case timer died in background
      }
    });
  }
}

/* ───────────────────── TOKEN helpers ─────────────────────── */

export const getAuthToken      = () => tokenCache;
export const checkHasAuthToken = () => Boolean(tokenCache);
export const getRefreshToken   = () => refreshTokenCache;

/**
 * Persist a credential.
 * Accepts either a raw JWT **string** or the full backend payload
 * `{ token, user, … }`.
 */
export async function setAuthToken(tokenObj) {
  const token = typeof tokenObj === 'string' ? tokenObj : tokenObj?.token;
  if (!token) return;

  const info  = typeof tokenObj === 'string'
    ? null
    : tokenObj.user ?? tokenObj.userInfo ?? null;
  const refreshToken = canPersistRefreshToken()
    ? (typeof tokenObj === 'string'
      ? refreshTokenCache
      : tokenObj.refresh_token ?? tokenObj.refreshToken ?? refreshTokenCache)
    : null;

  await persistState({ token, info, refreshToken, clearCreator: true });
  scheduleProactiveRefresh();
}

export async function refreshAccessTokenNow() {
  if (refreshInFlight) return refreshInFlight;
  if (!tokenCache) return false;

  refreshInFlight = (async () => {
    let refreshed = false;
    try {
      const response = await axios.post(
        `${BASE_API}/auth/refresh`,
        {},
        {
          headers: { Authorization: `Bearer ${tokenCache}` },
          withCredentials: true,
          timeout: 120000,
        }
      );

      const data = response?.data || {};
      const nextToken = data.access_token || data.token;
      if (!nextToken) return false;

      const nextRefresh = canPersistRefreshToken()
        ? (data.refresh_token ?? refreshTokenCache)
        : null;
      await persistState({
        token: nextToken,
        info: userInfoCache,
        refreshToken: nextRefresh,
        clearCreator: false,
      });

      scheduleProactiveRefresh(data.expires_in ?? null);
      refreshed = true;
      return true;
    } catch {
      return false;
    } finally {
      if (!refreshed && tokenCache) {
        const expMs = parseJwtExpMs(tokenCache);
        if (!expMs || expMs > Date.now()) {
          scheduleProactiveRefresh();
        }
      }
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function logoutAndClear() {
  const currentToken = tokenCache;

  try {
    if (currentToken) {
      await axios.post(
        `${BASE_API}/auth/logout`,
        {},
        {
          headers: { Authorization: `Bearer ${currentToken}` },
          withCredentials: true,
          timeout: 30000,
        }
      );
    }
  } catch {
    // best-effort logout; local cleanup still runs
  } finally {
    await removeAuthToken();
  }
}

/**
 * Clear *user* credentials (creator creds are left intact).
 *
 * NOTE: device_token cookie is intentionally NOT removed here.
 * Device tokens persist across logout to remember trusted devices.
 * This allows users to skip 2FA on subsequent logins from the same device.
 */
export async function removeAuthToken() {
  clearRefreshTimer();
  refreshInFlight = null;

  tokenCache    = null;
  userInfoCache = null;
  refreshTokenCache = null;

  if (Capacitor.isNativePlatform()) {
    await Promise.all([
      Preferences.remove({ key: KEY }),
      Preferences.remove({ key: USER_INFO_KEY }),
      Preferences.remove({ key: REFRESH_KEY }),
      // blitz_device_token intentionally NOT removed from localStorage
    ]);
  } else {
    Cookies.remove(KEY);           // Remove: blitz-auth-token
    Cookies.remove(USER_INFO_KEY); // Remove: user info
    Cookies.remove(REFRESH_KEY);   // Remove: refresh token
    // device_token cookie intentionally NOT removed - persists across logout
  }
}

/* ─────────────────── USER-info helpers ───────────────────── */

export const getAuthUserInfo = () => userInfoCache;

/** Update just the info blob. */
export async function setAuthUserInfo(info) {
  userInfoCache = info;
  const raw = JSON.stringify(info);

  if (Capacitor.isNativePlatform()) {
    await Preferences.set({ key: USER_INFO_KEY, value: raw });
  } else {
    Cookies.set(USER_INFO_KEY, raw,
      { expires: PERSIST_DAYS, secure: true, sameSite: 'Lax', path: '/' });
  }
  Cookies.remove(CREATOR_INFO_KEY);
}

/* ───────────────────── Misc helpers ─────────────────────── */

export const hasBothRoles = () =>
  Boolean(Cookies.get(KEY) && Cookies.get(CREATOR_TOKEN_KEY));

export const activeRole = () => {
  if (Cookies.get(KEY))              return 'user';
  if (Cookies.get(CREATOR_TOKEN_KEY)) return 'creator';
  return null;
};

/**
 * (Web only) Roll the 100-year window forward on every navigation.
 */
export function refreshCookieExpiry() {
  if (!Capacitor.isNativePlatform()) {
    if (tokenCache) {
      Cookies.set(KEY, tokenCache,
        { expires: PERSIST_DAYS, secure: true, sameSite: 'Lax', path: '/' });
    }
    if (userInfoCache) {
      Cookies.set(USER_INFO_KEY, JSON.stringify(userInfoCache),
        { expires: PERSIST_DAYS, secure: true, sameSite: 'Lax', path: '/' });
    }
  }
}

/* ─────────────────── internal helper ─────────────────────── */
/*  Called by creatorTokenUtil to wipe *this* role from RAM    */
export function clearMemory() {
  clearRefreshTimer();
  refreshInFlight = null;
  tokenCache    = null;
  userInfoCache = null;
  refreshTokenCache = null;
}
